/** GeboortedatumInfo — compacte inline info-bar bovenaan stap 1.
 *
 * Toont: "Geboortedatum: DD-MM-JJJJ (N jaar) · Wijzigen"
 * Klik 'Wijzigen' → inline date-input + Opslaan/Annuleer
 * Bij Opslaan: PATCH /api/werknemer/{token}/geboortedatum → onUpdate(nieuwe-defaults)
 *
 * Staat er nog géén geboortedatum (het defaults-eindpunt geeft dan een 404), dan vraagt
 * dit blok erom in plaats van niets te tonen. Zonder die tak liep de deelnemer vast:
 * de leeftijd is verplicht op stap 1, wordt afgeleid uit de geboortedatum, en er was
 * nergens een veld om hem in te vullen — de melding "Wat is uw leeftijd?" bleef staan
 * en er was geen weg vooruit. (Gevonden 2026-08-20.)
 */
import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiError, withRetry } from "../lib/api";

function formatNL(iso) {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}-${m}-${y}`;
}

export function GeboortedatumInfo({ defaults, progressieToken, onUpdate, ontbreekt = false }) {
  const [editing, setEditing] = useState(false);
  const [datum, setDatum] = useState(defaults?.geboortedatum?.slice(0, 10) || "");
  const [err, setErr] = useState(null);

  const mut = useMutation({
    mutationFn: async (nieuweDatum) => {
      const res = await withRetry(() => api.patch(`/werknemer/${progressieToken}/geboortedatum`, {
        geboortedatum: nieuweDatum,
      }));
      return res.data;
    },
    onSuccess: (data) => {
      setErr(null);
      setEditing(false);
      onUpdate?.(data);
    },
    onError: (e) => setErr(formatApiError(e)),
  });

  // `ontbreekt` betekent: de server zegt met zoveel woorden dat er geen geboortedatum
  // geregistreerd is (een 404 op het intake-eindpunt). Alleen dán erom vragen — bij een
  // storing of een netwerkfout zijn de gegevens er misschien wél, en zou dit blok de
  // opgeslagen datum stilletjes overschrijven met wat de deelnemer opnieuw intikt.
  if (!defaults && !ontbreekt) return null;

  if (!defaults) {
    return (
      <div data-testid="geboortedatum-ontbreekt" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
        <label htmlFor="geboortedatum-eerste" className="block text-xs font-medium text-slate-800">
          Wat is uw geboortedatum? <span className="text-rose-600">*</span>
        </label>
        <p className="mt-0.5 text-xs text-slate-600">
          Uw leeftijd rekenen we hieruit uit. We vragen er verder niet naar.
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <input
            id="geboortedatum-eerste"
            type="date"
            value={datum}
            onChange={(e) => setDatum(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            data-testid="input-geboortedatum-eerste"
            className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <button
            type="button"
            onClick={() => datum && mut.mutate(datum)}
            disabled={mut.isPending || !datum}
            data-testid="btn-geboortedatum-eerste-opslaan"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {mut.isPending ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
        {err ? <p className="mt-2 text-xs text-rose-600" data-testid="err-geboortedatum">{err.message || String(err)}</p> : null}
      </div>
    );
  }

  if (!editing) {
    return (
      <div data-testid="geboortedatum-info" className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <span>
          Geboortedatum: <span className="font-medium text-slate-800">{formatNL(defaults.geboortedatum)}</span>
          {" "}({defaults.leeftijd} jaar)
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="btn-geboortedatum-wijzigen"
          className="text-blue-600 hover:underline"
        >
          Wijzigen
        </button>
      </div>
    );
  }

  return (
    <div data-testid="geboortedatum-info-edit" className="mb-4 rounded-md border border-slate-200 bg-white p-3">
      <label className="block text-xs font-medium text-slate-700">Geboortedatum wijzigen</label>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <input
          type="date"
          value={datum}
          onChange={(e) => setDatum(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
          data-testid="input-geboortedatum-wijzig"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <button
          type="button"
          onClick={() => datum && mut.mutate(datum)}
          disabled={mut.isPending || !datum}
          data-testid="btn-geboortedatum-opslaan"
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {mut.isPending ? "Opslaan…" : "Opslaan"}
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setDatum(defaults?.geboortedatum?.slice(0, 10) || ""); setErr(null); }}
          data-testid="btn-geboortedatum-annuleer"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          Annuleer
        </button>
      </div>
      {err ? <p className="mt-2 text-xs text-rose-600" data-testid="err-geboortedatum">{err.message || String(err)}</p> : null}
    </div>
  );
}
